import { forwardRef, useRef, useState } from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Icon } from "./icon";
import {
  phoneInputControlVariants,
  phoneInputTriggerVariants,
  type PhoneInputControlVariants,
  type PhoneInputTriggerVariants,
} from "./phone-input.styles";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "./popover";
import { ScrollArea, type ScrollAreaRef } from "./scroll-area";
import { cx, formatAriaInvalid } from "./style.utils";

// -----------------------------------------------------------------------------

export type PhoneInputFlagIconProps = Pick<
  RPNInput.FlagProps,
  "country" | "countryName"
>;

export interface PhoneInputFlagProps
  extends
    Omit<React.ComponentPropsWithoutRef<"span">, "children">,
    PhoneInputFlagIconProps {}

/**
 * Displays a flag icon for the
 * selected country (`PhoneInputTrigger`)
 * and the selectable options (`PhoneInputOption`).
 */
export function PhoneInputFlag({
  className,
  country,
  countryName,
  ...props
}: PhoneInputFlagProps) {
  const Comp = flags[country];
  return (
    <span {...props} className={cx("min-w-0 max-w-18px shrink-0", className)}>
      {Comp ? <Comp title={countryName} /> : null}
    </span>
  );
}

// -----------------------------------------------------------------------------

export type PhoneInputTriggerRef = React.ComponentRef<"button">;

export interface PhoneInputTriggerProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "children">,
    PhoneInputFlagIconProps,
    PhoneInputTriggerVariants {}

/**
 * A button component that displays a flag icon for the selected country.
 * It is used as a trigger for the phone input select popover.
 */
const PhoneInputTrigger = forwardRef<
  PhoneInputTriggerRef,
  PhoneInputTriggerProps
>(
  (
    {
      className,
      country,
      countryName,
      disabled,
      invalid,
      active,
      size,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        {...props}
        type="button"
        disabled={disabled}
        className={phoneInputTriggerVariants({
          className,
          disabled,
          invalid,
          active,
          size,
        })}
        ref={ref}
      >
        <PhoneInputFlag country={country} countryName={countryName} />
      </button>
    );
  },
);

PhoneInputTrigger.displayName = "PhoneInputTrigger";

export { PhoneInputTrigger };

// -----------------------------------------------------------------------------

/**
 * A select component for choosing a country.
 * It displays a list of countries with their flags and calling codes.
 * It uses a popover to show the list of countries.
 * The selected country is displayed in the `PhoneInputTrigger`.
 */
export function PhoneInputSelect({
  disabled,
  invalid,
  onChange,
  options,
  size,
  value: selectedCountry,
}: {
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  onChange: (country: RPNInput.Country) => void;
  options: Array<{ label: string; value: RPNInput.Country | undefined }>;
  size?: "sm" | "md" | "lg" | undefined;
  value: RPNInput.Country;
}) {
  const [isOpen, setIsOpen] = useState(false);

  // State to manage the search input value
  const [searchValue, setSearchValue] = useState("");
  const scrollAreaRef = useRef<ScrollAreaRef>(null);

  // Sets the selected flag `title` for a11y
  const countryName =
    options.find((el) => el.value === selectedCountry)?.label ||
    "Select a country";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <PhoneInputTrigger
          country={selectedCountry}
          countryName={countryName}
          active={isOpen}
          disabled={disabled}
          invalid={invalid}
          size={size}
        />
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="w-320px rounded-lg p-0"
          align="start" // Aligns the popover to the left side of the trigger
          alignOffset={-4} // Aligns the selected flag, search icon, and option flags
        >
          <Command>
            <CommandInput
              placeholder="Search countries…"
              value={searchValue}
              onValueChange={(value: string) => {
                setSearchValue(value);
                // Reset scroll position when search input changes.
                setTimeout(() => {
                  if (scrollAreaRef.current) {
                    const viewportElement = scrollAreaRef.current.querySelector(
                      "[data-radix-scroll-area-viewport]",
                    );
                    if (viewportElement) {
                      viewportElement.scrollTop = 0;
                    }
                  }
                }, 0);
              }}
            />
            <CommandList>
              <ScrollArea className="h-180px" ref={scrollAreaRef}>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {options.map(({ value, label }) =>
                    value ? (
                      <PhoneInputOption
                        country={value}
                        countryName={label}
                        selectedCountry={selectedCountry}
                        onChange={onChange}
                        onSelectComplete={() => setIsOpen(false)}
                        key={value}
                      />
                    ) : null,
                  )}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

// -----------------------------------------------------------------------------

/**
 * A command item used in the `PhoneInputSelect` component
 * that represents a selectable country option.
 * It displays the flag, country name, calling code,
 * and a check icon if the country is selected.
 */
export function PhoneInputOption({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: RPNInput.FlagProps & {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}) {
  return (
    <CommandItem
      onSelect={() => {
        onChange(country);
        onSelectComplete();
      }}
    >
      <PhoneInputFlag
        className="translate-x-1px"
        country={country}
        countryName={countryName}
      />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-shade-80">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <Icon
        name="ms-check"
        className={cx(
          "ml-auto",
          country === selectedCountry ? "opacity-100" : "opacity-0",
        )}
      />
    </CommandItem>
  );
}

// -----------------------------------------------------------------------------

export type PhoneInputControlRef = React.ComponentRef<"input">;

export interface PhoneInputControlProps
  extends
    Omit<React.ComponentPropsWithoutRef<"input">, "size">,
    Omit<PhoneInputControlVariants, "invalid"> {
  name: string;
  type?: "tel" | "text";
}

/**
 * A custom input component for `PhoneInput`.
 */
const PhoneInputControl = forwardRef<
  PhoneInputControlRef,
  PhoneInputControlProps
>(
  (
    {
      "aria-invalid": ariaInvalid,
      className,
      disabled,
      size,
      type = "tel",
      ...props
    },
    ref,
  ) => {
    return (
      <input
        {...props}
        type={type}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        className={phoneInputControlVariants({
          className,
          disabled,
          invalid: formatAriaInvalid(ariaInvalid),
          size,
        })}
        ref={ref}
      />
    );
  },
);

PhoneInputControl.displayName = "PhoneInputControl";

export { PhoneInputControl };
